import {isEmpty} from "@pg-mono/nodash";

import {getOfferTypeNamePlural} from "../../offer/helpers/OfferType";
import {getAnalyticsSortDescription} from "../../offer/list/helpers/handle_fetch_offer_list_gtm_events";
import {ITravelTimeStore} from "../../search/actions/set_travel_time";
import {RegionSearchTabsType} from "../../search/types/RegionSearchTabsType";
import {ILatestQuery, parseQueryParamToInt} from "../../utils/latest_query";
import {ViewType} from "../../view_type/ViewType";
import {OfferListSearchData} from "../types/OfferListSearchData";
import {getTrackedSiteData} from "./tracked_utils/tracked_site";
import {searchHitAlgolytics} from "./search_algolytics";

export const offerListSearchHit = (
    viewType: string,
    searchData: OfferListSearchData,
    offerIds: number[],
    offerNames: string[],
    vendorIds: number[],
    vendorNames: string[]
) => {
    const payload = {
        view_type: viewType,
        ...getTrackedSiteData(),
        ...searchData,
        offer_id: offerIds,
        offer_name: offerNames,
        vendor_id: vendorIds,
        vendor_name: vendorNames
    };
    searchHitAlgolytics(payload);
};

/**
 * Helper
 */
interface ISearchRegionsData {
    regions: string[];
    regions_types: string[];
    regions_types_ids: number[];
}

export interface ISearchRegion {
    short_name: string;
    type: number;
    type__display: string; // additional field based on `variables.region_type`
}

export const prepareOfferListSearchData = (params: {
    offers_count: number;
    properties_count: number | null;
    latestQuery: ILatestQuery;
    searchRegions: ISearchRegion[];
    query: Record<string, string | string[]>; // needed in case of `s` query-param is present
    sourceView?: ViewType | null; //previous view type
    travel_time?: ITravelTimeStore | null;
    locationSearchTab?: RegionSearchTabsType | null;
}): OfferListSearchData => {
    const {offers_count, properties_count, latestQuery, searchRegions, query, sourceView, travel_time, locationSearchTab} = params;
    // initial data
    const searchData: Partial<OfferListSearchData> = {
        search_offers_count: offers_count,
        search_luxury: !!latestQuery.is_luxury,
        search_properties_count: properties_count
    };
    // region
    if (latestQuery.region) {
        const regionsStr = Array.isArray(latestQuery.region) ? latestQuery.region : [latestQuery.region];
        const regions = regionsStr.map((r) => parseInt(r, 10));
        if (regions.length > 1) {
            const searchRegionsData = searchRegions.reduce(
                (acc: ISearchRegionsData, region) => {
                    return {
                        regions: [...acc.regions, region.short_name],
                        regions_types: [...acc.regions_types, region.type__display],
                        regions_types_ids: [...acc.regions_types_ids, region.type]
                    };
                },
                {
                    regions: [],
                    regions_types: [],
                    regions_types_ids: []
                }
            );

            // no single region data
            searchData.search_region = null;
            searchData.search_region_id = null;
            searchData.search_region_type = null;
            searchData.search_region_type_id = null;
            searchData.search_regions = searchRegionsData.regions;
            searchData.search_regions_ids = regions;
            searchData.search_regions_types_ids = searchRegionsData.regions_types_ids;
        } else {
            if (isFinite(regions[0])) {
                // single region data
                searchData.search_region = searchRegions.length > 0 ? searchRegions[0].short_name : null;
                searchData.search_region_id = regions[0];
                searchData.search_region_type = searchRegions.length > 0 ? searchRegions[0].type__display : null;
                searchData.search_region_type_id = searchRegions.length > 0 ? searchRegions[0].type : null;
                // no multi region data
                searchData.search_regions = [];
                searchData.search_regions_ids = [];
                searchData.search_regions_types_ids = [];
            }
        }
    }

    // offer
    const offer = parseQueryParamToInt(latestQuery.near_by_offer);
    if (offer) {
        searchData.search_offer_id = offer;
    }
    // vendor
    const vendor = parseQueryParamToInt(latestQuery.vendor);
    if (vendor) {
        searchData.search_vendor_id = vendor;
    }
    // google place
    if (latestQuery.lat && latestQuery.lng) {
        if (query?.s) {
            searchData.search_google_place = query.s;
        }
    }
    // offer type
    const offerType = parseQueryParamToInt(latestQuery.type);
    if (offerType) {
        searchData.search_type = getOfferTypeNamePlural(offerType);
        searchData.search_type_id = offerType;
    } else {
        searchData.search_type = "mieszkania i domy";
    }
    // price
    const priceLower = parseQueryParamToInt(latestQuery.price_0);
    if (priceLower) {
        searchData.search_price_lower = priceLower;
    }

    const priceUpper = parseQueryParamToInt(latestQuery.price_1);
    if (priceUpper) {
        searchData.search_price_upper = priceUpper;
    }
    // rooms
    const roomsLower = parseQueryParamToInt(latestQuery.rooms_0);
    if (roomsLower) {
        searchData.search_rooms_lower = roomsLower;
    }

    const roomsUpper = parseQueryParamToInt(latestQuery.rooms_1);
    if (roomsUpper) {
        searchData.search_rooms_upper = roomsUpper;
    }
    // area
    const areaLower = parseQueryParamToInt(latestQuery.area_0);
    if (areaLower) {
        searchData.search_area_lower = areaLower;
    }
    const areaUpper = parseQueryParamToInt(latestQuery.area_1);
    if (areaUpper) {
        searchData.search_area_upper = areaUpper;
    }
    // sort
    const sortDescription = getAnalyticsSortDescription(latestQuery.sort as string);
    if (sortDescription) {
        searchData.search_sort = sortDescription;
    }
    // price_m2
    const priceM2Lower = parseQueryParamToInt(latestQuery.price_m2_0);
    if (priceM2Lower) {
        searchData.search_price_m2_lower = priceM2Lower;
    }
    const priceM2Upper = parseQueryParamToInt(latestQuery.price_m2_1);
    if (priceM2Upper) {
        searchData.search_price_m2_upper = priceM2Upper;
    }
    // floor
    if (latestQuery.floor_choices) {
        const floorChoices = Array.isArray(latestQuery.floor_choices) ? latestQuery.floor_choices : [latestQuery.floor_choices];
        searchData.additional_info = {
            search_floors_dict: floorChoices.map((floor) => parseInt(floor))
        };
    }

    if (latestQuery.instalment && !isNaN(parseInt(latestQuery.instalment))) {
        searchData.additional_info = {...searchData.additional_info, search_installment: parseInt(latestQuery.instalment)};
    }
    // construction_end_date
    const constructionEndDate = parseQueryParamToInt(latestQuery.construction_end_date);
    if (constructionEndDate) {
        searchData.search_construction_end_date = constructionEndDate;
    }
    // additional_areas
    const desktopAdditionalAreas = latestQuery.property_additional_areas?.map(Number);
    const mobileAdditionalAreas = latestQuery.additional_areas?.map(Number);
    if (!isEmpty(desktopAdditionalAreas)) {
        searchData.search_additional_areas = desktopAdditionalAreas;
    } else if (!isEmpty(mobileAdditionalAreas)) {
        searchData.search_additional_areas = mobileAdditionalAreas;
    }
    // facilities
    const facilities = Array.isArray(latestQuery.facilities) ? latestQuery.facilities.map(Number) : [Number(latestQuery.facilities)];
    if (!isEmpty(facilities)) {
        searchData.search_facilities = facilities;
    }
    // offer_size
    const offerSize = parseQueryParamToInt(latestQuery.offer_size);
    if (offerSize) {
        searchData.search_offer_size = offerSize;
    }
    // source view
    if (sourceView) {
        searchData.search_source_view = sourceView;
    }
    //travel_time
    if (locationSearchTab === RegionSearchTabsType.TRAVEL_TIME && travel_time) {
        searchData.travel_time = travel_time.data;
    }

    return searchData as OfferListSearchData;
};
import {isEmpty} from "@pg-mono/nodash";

import {getOfferTypeNamePlural} from "../../offer/helpers/OfferType";
import {getAnalyticsSortDescription} from "../../offer/list/helpers/handle_fetch_offer_list_gtm_events";
import {ITravelTimeStore} from "../../search/actions/set_travel_time";
import {RegionSearchTabsType} from "../../search/types/RegionSearchTabsType";
import {ILatestQuery, parseQueryParamToInt} from "../../utils/latest_query";
import {ViewType} from "../../view_type/ViewType";
import {OfferListSearchData} from "../types/OfferListSearchData";
import {getTrackedSiteData} from "./tracked_utils/tracked_site";
import {searchHitAlgolytics} from "./search_algolytics";

export const offerListSearchHit = (
    viewType: string,
    searchData: OfferListSearchData,
    offerIds: number[],
    offerNames: string[],
    vendorIds: number[],
    vendorNames: string[]
) => {
    const payload = {
        view_type: viewType,
        ...getTrackedSiteData(),
        ...searchData,
        offer_id: offerIds,
        offer_name: offerNames,
        vendor_id: vendorIds,
        vendor_name: vendorNames
    };
    searchHitAlgolytics(payload);
};

/**
 * Helper
 */
interface ISearchRegionsData {
    regions: string[];
    regions_types: string[];
    regions_types_ids: number[];
}

export interface ISearchRegion {
    short_name: string;
    type: number;
    type__display: string; // additional field based on `variables.region_type`
}

export const prepareOfferListSearchData = (params: {
    offers_count: number;
    properties_count: number | null;
    latestQuery: ILatestQuery;
    searchRegions: ISearchRegion[];
    query: Record<string, string | string[]>; // needed in case of `s` query-param is present
    sourceView?: ViewType | null; //previous view type
    travel_time?: ITravelTimeStore | null;
    locationSearchTab?: RegionSearchTabsType | null;
}): OfferListSearchData => {
    const {offers_count, properties_count, latestQuery, searchRegions, query, sourceView, travel_time, locationSearchTab} = params;
    // initial data
    const searchData: Partial<OfferListSearchData> = {
        search_offers_count: offers_count,
        search_luxury: !!latestQuery.is_luxury,
        search_properties_count: properties_count
    };
    // region
    if (latestQuery.region) {
        const regionsStr = Array.isArray(latestQuery.region) ? latestQuery.region : [latestQuery.region];
        const regions = regionsStr.map((r) => parseInt(r, 10));
        if (regions.length > 1) {
            const searchRegionsData = searchRegions.reduce(
                (acc: ISearchRegionsData, region) => {
                    return {
                        regions: [...acc.regions, region.short_name],
                        regions_types: [...acc.regions_types, region.type__display],
                        regions_types_ids: [...acc.regions_types_ids, region.type]
                    };
                },
                {
                    regions: [],
                    regions_types: [],
                    regions_types_ids: []
                }
            );

            // no single region data
            searchData.search_region = null;
            searchData.search_region_id = null;
            searchData.search_region_type = null;
            searchData.search_region_type_id = null;
            searchData.search_regions = searchRegionsData.regions;
            searchData.search_regions_ids = regions;
            searchData.search_regions_types_ids = searchRegionsData.regions_types_ids;
        } else {
            if (isFinite(regions[0])) {
                // single region data
                searchData.search_region = searchRegions.length > 0 ? searchRegions[0].short_name : null;
                searchData.search_region_id = regions[0];
                searchData.search_region_type = searchRegions.length > 0 ? searchRegions[0].type__display : null;
                searchData.search_region_type_id = searchRegions.length > 0 ? searchRegions[0].type : null;
                // no multi region data
                searchData.search_regions = [];
                searchData.search_regions_ids = [];
                searchData.search_regions_types_ids = [];
            }
        }
    }

    // offer
    const offer = parseQueryParamToInt(latestQuery.near_by_offer);
    if (offer) {
        searchData.search_offer_id = offer;
    }
    // vendor
    const vendor = parseQueryParamToInt(latestQuery.vendor);
    if (vendor) {
        searchData.search_vendor_id = vendor;
    }
    // google place
    if (latestQuery.lat && latestQuery.lng) {
        if (query?.s) {
            searchData.search_google_place = query.s;
        }
    }
    // offer type
    const offerType = parseQueryParamToInt(latestQuery.type);
    if (offerType) {
        searchData.search_type = getOfferTypeNamePlural(offerType);
        searchData.search_type_id = offerType;
    } else {
        searchData.search_type = "mieszkania i domy";
    }
    // price
    const priceLower = parseQueryParamToInt(latestQuery.price_0);
    if (priceLower) {
        searchData.search_price_lower = priceLower;
    }

    const priceUpper = parseQueryParamToInt(latestQuery.price_1);
    if (priceUpper) {
        searchData.search_price_upper = priceUpper;
    }
    // rooms
    const roomsLower = parseQueryParamToInt(latestQuery.rooms_0);
    if (roomsLower) {
        searchData.search_rooms_lower = roomsLower;
    }

    const roomsUpper = parseQueryParamToInt(latestQuery.rooms_1);
    if (roomsUpper) {
        searchData.search_rooms_upper = roomsUpper;
    }
    // area
    const areaLower = parseQueryParamToInt(latestQuery.area_0);
    if (areaLower) {
        searchData.search_area_lower = areaLower;
    }
    const areaUpper = parseQueryParamToInt(latestQuery.area_1);
    if (areaUpper) {
        searchData.search_area_upper = areaUpper;
    }
    // sort
    const sortDescription = getAnalyticsSortDescription(latestQuery.sort as string);
    if (sortDescription) {
        searchData.search_sort = sortDescription;
    }
    // price_m2
    const priceM2Lower = parseQueryParamToInt(latestQuery.price_m2_0);
    if (priceM2Lower) {
        searchData.search_price_m2_lower = priceM2Lower;
    }
    const priceM2Upper = parseQueryParamToInt(latestQuery.price_m2_1);
    if (priceM2Upper) {
        searchData.search_price_m2_upper = priceM2Upper;
    }
    // floor
    if (latestQuery.floor_choices) {
        const floorChoices = Array.isArray(latestQuery.floor_choices) ? latestQuery.floor_choices : [latestQuery.floor_choices];
        searchData.additional_info = {
            search_floors_dict: floorChoices.map((floor) => parseInt(floor))
        };
    }

    if (latestQuery.instalment && !isNaN(parseInt(latestQuery.instalment))) {
        searchData.additional_info = {...searchData.additional_info, search_installment: parseInt(latestQuery.instalment)};
    }
    // construction_end_date
    const constructionEndDate = parseQueryParamToInt(latestQuery.construction_end_date);
    if (constructionEndDate) {
        searchData.search_construction_end_date = constructionEndDate;
    }
    // additional_areas
    const desktopAdditionalAreas = latestQuery.property_additional_areas?.map(Number);
    const mobileAdditionalAreas = latestQuery.additional_areas?.map(Number);
    if (!isEmpty(desktopAdditionalAreas)) {
        searchData.search_additional_areas = desktopAdditionalAreas;
    } else if (!isEmpty(mobileAdditionalAreas)) {
        searchData.search_additional_areas = mobileAdditionalAreas;
    }
    // facilities
    const facilities = Array.isArray(latestQuery.facilities) ? latestQuery.facilities.map(Number) : [Number(latestQuery.facilities)];
    if (!isEmpty(facilities)) {
        searchData.search_facilities = facilities;
    }
    // offer_size
    const offerSize = parseQueryParamToInt(latestQuery.offer_size);
    if (offerSize) {
        searchData.search_offer_size = offerSize;
    }
    // source view
    if (sourceView) {
        searchData.search_source_view = sourceView;
    }
    //travel_time
    if (locationSearchTab === RegionSearchTabsType.TRAVEL_TIME && travel_time) {
        searchData.travel_time = travel_time.data;
    }

    return searchData as OfferListSearchData;
};
